import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { NotificationContainerComponent } from '@core/components/notification-container/notification-container.component';
import { OverlayHostComponent } from '@core/components/overlay-host/overlay-host.component';
import { TopBarComponent } from '@core/components/top-bar/top-bar.component';
import { UserPreferencesActions } from '@core/store/user-preferences/user-preferences.actions';
import { selectSidebarStyle } from '@core/store/user-preferences/user-preferences.selectors';

@Component({
  selector: 'app-shell-base',
  imports: [TopBarComponent, NotificationContainerComponent, OverlayHostComponent],
  templateUrl: './app-shell-base.component.html'
})
export class AppShellBaseComponent {
  private readonly store = inject(Store);

  /** Whether to show the dataspace dropdown in the top bar */
  showDataspaceDropdown = input<boolean>(true);

  /** Whether the sidebar is open (two-way bindable via sidebarOpenChange) */
  sidebarOpen = signal(true);

  private readonly sidebarStyleSignal = toSignal(this.store.select(selectSidebarStyle), {
    initialValue: 'extended' as const
  });

  sidebarCollapsed = computed(() => this.sidebarStyleSignal() === 'collapsed');

  /** Emitted when the sidebar toggle is clicked from the top bar */
  sidebarOpenChange = output<boolean>();

  constructor() {
    this.sidebarOpen.set(this.getInitialSidebarState());
  }

  toggleSidebar(): void {
    this.sidebarOpen.update((v) => !v);
    this.sidebarOpenChange.emit(this.sidebarOpen());
  }

  toggleCollapsed(): void {
    const next = this.sidebarCollapsed() ? 'extended' : 'collapsed';
    this.store.dispatch(UserPreferencesActions.setSidebarStyle({ style: next }));
  }

  onSidebarHeaderClick(): void {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      this.toggleCollapsed();
    } else {
      this.toggleSidebar();
    }
  }

  private getInitialSidebarState(): boolean {
    return typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 1024px)').matches
      : true;
  }
}
